import React, { useEffect, useMemo } from 'react';
import useAppStore from '../../store/useAppStore.js';
import ReportCard from '../common/ReportCard.js';
import ProductTable from './ProductTable.js';
import ProductDetailsDialog from './ProductDetailsDialog.js';
import { formatCurrencyShort } from '../../utils/amountHelpers.js';

const formatCurrency = formatCurrencyShort;

const formatNumber = (value) =>
  new Intl.NumberFormat('en-US').format(value || 0);

const parseBranchId = (branchValue) => {
  if (!branchValue || branchValue === '') return null;
  const normalized = branchValue.toString().replace(/^BR-/, '');
  const numeric = Number(normalized);
  return Number.isNaN(numeric) ? null : numeric;
};

const ProductReports = () => {
  const {
    admin,
    productReports,
    loadBranches,
    loadProductReports,
    setProductReportsDate,
    setProductReportFilters,
    setSelectedProductReport,
    setProductReportDialogOpen,
    clearProductReportErrors
  } = useAppStore((state) => ({
    admin: state.admin,
    productReports: state.admin.productReports,
    loadBranches: state.admin.loadBranches,
    loadProductReports: state.admin.loadProductReports,
    setProductReportsDate: state.admin.setProductReportsDate,
    setProductReportFilters: state.admin.setProductReportFilters,
    setSelectedProductReport: state.admin.setSelectedProductReport,
    setProductReportDialogOpen: state.admin.setProductReportDialogOpen,
    clearProductReportErrors: state.admin.clearProductReportErrors
  }));

  useEffect(() => {
    if (!admin.branches || admin.branches.length === 0) {
      loadBranches();
    }
  }, [admin.branches, loadBranches]);

  useEffect(() => {
    clearProductReportErrors();
    const branchId = parseBranchId(productReports.filters.branchId);
    loadProductReports(productReports.selectedDate, branchId);
  }, [productReports.selectedDate, productReports.filters.branchId, loadProductReports, clearProductReportErrors]);

  const branchOptions = useMemo(
    () => [{ label: 'All branches', value: '' }, ...(admin.branches || []).map((branch) => ({ label: branch.name, value: branch.id }))],
    [admin.branches]
  );

  const categoryOptions = useMemo(() => {
    const categories = new Set((productReports.products || []).map((product) => product.category || 'Uncategorized'));
    return [{ label: 'All categories', value: '' }, ...Array.from(categories).sort().map((category) => ({ label: category, value: category }))];
  }, [productReports.products]);

  const filteredProducts = useMemo(() => {
    const nameFilter = productReports.filters.name.trim().toLowerCase();
    const categoryFilter = productReports.filters.category.trim().toLowerCase();
    const branchFilter = parseBranchId(productReports.filters.branchId);

    return (productReports.products || []).filter((product) => {
      const matchesName = !nameFilter || (product.name || '').toLowerCase().includes(nameFilter);
      const matchesCategory = !categoryFilter || (product.category || 'Uncategorized').toLowerCase().includes(categoryFilter);
      const matchesBranch = !branchFilter || product.branchId === branchFilter;
      return matchesName && matchesCategory && matchesBranch;
    });
  }, [productReports.products, productReports.filters]);

  const summary = useMemo(() => {
    const totalProducts = filteredProducts.length;
    const totalRevenue = filteredProducts.reduce((sum, product) => sum + (product.totalRevenue || 0), 0);
    const totalQuantity = filteredProducts.reduce((sum, product) => sum + (product.totalOrders || 0), 0);
    const avgRevenue = totalProducts > 0 ? totalRevenue / totalProducts : 0;

    return {
      totalProducts,
      totalRevenue,
      totalQuantity,
      avgRevenue
    };
  }, [filteredProducts]);

  const handleViewDetails = (product) => {
    setSelectedProductReport(product);
    setProductReportDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setProductReportDialogOpen(false);
    setSelectedProductReport(null);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Product Reports</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">
            Comprehensive analysis of product performance across all branches with search, branch, and category filters.
          </p>
        </div>
        <div className="space-y-2 rounded-3xl border border-gray-200 bg-white p-4 shadow-sm">
          <label htmlFor="productReportDate" className="block text-sm font-medium text-gray-700">Report date</label>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              id="productReportDate"
              type="date"
              value={productReports.selectedDate || ''}
              onChange={(e) => setProductReportsDate(e.target.value)}
              className="w-full rounded-3xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
            <button
              type="button"
              onClick={() => setProductReportsDate('')}
              className="inline-flex shrink-0 items-center justify-center rounded-3xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              All days
            </button>
          </div>
          <p className="text-sm text-gray-500">
            {productReports.selectedDate ? 'Showing sales for the selected day.' : 'Showing sales for all days.'}
          </p>
        </div>
      </div>

      {productReports.error && (
        <div className="rounded-3xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{productReports.error}</div>
      )}

      <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-1">
            <label htmlFor="productSearch" className="block text-sm font-medium text-gray-700">Search product</label>
            <input
              id="productSearch"
              value={productReports.filters.name}
              onChange={(e) => setProductReportFilters({ name: e.target.value })}
              placeholder="Product name"
              className="w-full rounded-3xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="branchFilter" className="block text-sm font-medium text-gray-700">Branch</label>
            <select
              id="branchFilter"
              value={productReports.filters.branchId}
              onChange={(e) => setProductReportFilters({ branchId: e.target.value })}
              className="w-full rounded-3xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              {branchOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="categoryFilter" className="block text-sm font-medium text-gray-700">Category</label>
            <select
              id="categoryFilter"
              value={productReports.filters.category}
              onChange={(e) => setProductReportFilters({ category: e.target.value })}
              className="w-full rounded-3xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              {categoryOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        <ReportCard title="Total products" value={summary.totalProducts} subtitle="Products matching filters" />
        <ReportCard title="Total revenue" value={formatCurrency(summary.totalRevenue)} subtitle="Revenue from filtered products" />
        <ReportCard title="Total quantity" value={formatNumber(summary.totalQuantity)} subtitle="Units sold in filtered set" />
        <ReportCard title="Avg revenue/product" value={formatCurrency(summary.avgRevenue)} subtitle="Average revenue per product" />
      </div>

      <ProductTable
        products={filteredProducts}
        loading={productReports.loading}
        onViewDetails={handleViewDetails}
      />

      <ProductDetailsDialog
        product={productReports.selectedProduct}
        isOpen={productReports.detailDialogOpen}
        onClose={handleCloseDetails}
      />
    </div>
  );
};

export default ProductReports;